import moment from 'moment';
import config from 'config';
import { isEmpty, isNaN, size } from 'lodash';
import { CSV_META, ORDER_PRIORITY } from './csv/config';
import { getVRPTimes, getVRPVehicleDefinition } from './VRPConverterTourPlanner';
import { getObjectivesLimit } from './solverConfig/config';
import { groupJobsPerTerritory, isOrderInTerritory } from './territories/TerritoriesHelpers';
import { isProdEnv } from './helpers';
import { APP_MODES, getAppMode } from './urlHelpers';

const { dateTimeFormat, defaults } = config;
const parseCoordinateValue = (value) =>
  typeof value === 'number' ? value : parseFloat(value.replace(',', '.'));

const parseRowTimes = (row) => {
  const knownDateFormats = [CSV_META.timeFormat];
  const timeRegexStart = /(\d?\d:\d\d)/g;
  const timeRegexEnd = /(\d?\d:\d\d)/g;
  const startTime = timeRegexStart.exec(row.StartTime)[1];
  const endTime = timeRegexEnd.exec(row.EndTime)[1];

  return [moment(startTime, knownDateFormats).format(), moment(endTime, knownDateFormats).format()];
};

const createJobPlaceFromRow = (row, territories) => {
  const place = {
    duration:
      !isNaN(parseInt(row.ServiceTime, 10)) && parseInt(row.ServiceTime, 10) >= 0
        ? parseInt(row.ServiceTime, 10) * 60
        : defaults.serviceTime,
    location: {
      lat: parseCoordinateValue(row.Latitude),
      lng: parseCoordinateValue(row.Longitude),
    },
    ...(row.Group !== undefined && { groupId: row.Group }),
    ...(row.StartTime && row.EndTime && { times: [parseRowTimes(row)] }),
    ...(row?.Territories !== undefined && { territoryIds: row.Territories }),
  };

  const territory = isOrderInTerritory([row], territories);
  if (territory) place.territoryIds = [territory.name];

  return place;
};

const createTasksPerRow = (row, territories) => {
  const activityType = row.Activity;
  const places = [createJobPlaceFromRow(row, territories)];
  const demand =
    !isNaN(parseInt(row.Demand, 10)) && parseInt(row.Demand, 10) > 0
      ? [parseInt(row.Demand, 10)]
      : [defaults.demand];

  let position;
  if (row?.Position) {
    if (row.Position === 'first') {
      position = { type: 'first' };
    } else if (row.Position === 'last') {
      position = { type: 'last' };
    } else if (row.Position === 'any') {
      position = { type: 'any' };
    } else {
      position = { type: 'ordered', value: row.PositionValue || row.Position };
    }
  }

  const task = { places, demand };
  if (position) task.position = position;

  const tasks = {};
  if (activityType === 'pickup') tasks.pickups = [task];
  else tasks.deliveries = [task];

  return tasks;
};

export function mergeJobs(jobs) {
  const mergedJobs = {};

  jobs.forEach((job) => {
    const { id, tasks, skills, priority, category, maxTimeOnVehicle } = job;

    if (!mergedJobs[id]) {
      mergedJobs[id] = { id, tasks: {} };
      if (skills !== undefined) {
        mergedJobs[id].skills = skills;
      }
      if (priority !== undefined) {
        mergedJobs[id].priority = priority;
      }
      if (category !== undefined) {
        mergedJobs[id].category = category;
      }
      if (maxTimeOnVehicle !== undefined) {
        mergedJobs[id].maxTimeOnVehicle = maxTimeOnVehicle;
      }
    }

    Object.entries(tasks).forEach(([taskType, taskArray]) => {
      if (!mergedJobs[id].tasks[taskType]) {
        mergedJobs[id].tasks[taskType] = [];
      }
      mergedJobs[id].tasks[taskType].push(...taskArray);
    });
  });

  return Object.values(mergedJobs);
}
const parseNumeric = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return value;
  const num = Number(String(value).replace(',', '.'));
  return Number.isNaN(num) ? undefined : num;
};

export function convertParsedCSVToVRPJobs(data = [], isClustered, territories) {
  const filterPudos = data.filter((item) => item.Activity !== 'pudo');

  return filterPudos.map((row) => {
    const skillValue = Array.isArray(row.Skills) ? row.Skills[0] : row.Skills;
    const maxTimeNum = parseNumeric(row.MaxTime);

    return {
      id: isClustered ? row.ClusterID : row.InternalID,
      ...(row.Priority ? { priority: row.Priority } : {}),
      ...(skillValue ? { skills: [skillValue] } : {}),
      ...(row.Category ? { category: row.Category } : {}),
      ...(maxTimeNum !== undefined ? { maxTimeOnVehicle: maxTimeNum } : {}),
      tasks: createTasksPerRow(row, territories),
    };
  });
}

const getDepotLocation = (depot) => {
  if (!depot) return null;
  if (depot.lat) return depot;
  if (!depot.tasks) return null;

  const firstTask = depot.tasks.deliveries || depot.tasks.pickups;
  return firstTask[0].places[0].location;
};

const jobsContainPriority = (jobs) => {
  const jobsWithPriority = jobs.filter(
    (job) => job.priority !== undefined && job.priority !== ORDER_PRIORITY.NORMAL,
  );
  return size(jobsWithPriority) > 0;
};

const getObjectiveFunction = (item) => {
  const itemObj = { type: item.key };
  if (item.threshold !== undefined) itemObj.options = { threshold: item.threshold };
  if (item.action !== undefined) itemObj.action = item.action;
  return itemObj;
};

const getObjectiveFunctions = (tourPlanner, jobs, territories) => {
  if (!tourPlanner.solverConfiguration.objectiveFunctions.enabled) return null;

  const hasPrioJobs = jobsContainPriority(jobs);
  let allItems = tourPlanner.solverConfiguration.objectiveFunctions.values.filter((v) => v.enabled);
  if (!hasPrioJobs) {
    allItems = allItems.filter((item) => item.key !== 'maximizePriorityJobs');
  } else {
    const index = allItems.findIndex((item) => item.key === 'maximizePriorityJobs');
    if (index === -1) allItems.unshift({ key: 'maximizePriorityJobs', enabled: true });
  }

  if (isEmpty(territories)) {
    allItems = allItems.filter((item) => item.key !== 'maximizeTerritoryJobs');
  } else {
    const index = allItems.findIndex((item) => item.key === 'maximizeTerritoryJobs');
    if (index === -1) allItems.unshift({ key: 'maximizeTerritoryJobs', enabled: true });
  }

  const balanceObjectiveIndex = allItems.findIndex((item) => item.balance === true);
  const hasBalanceObjective = balanceObjectiveIndex !== -1;
  const balanceObjective = allItems.splice(balanceObjectiveIndex, balanceObjectiveIndex + 1)[0];

  const limit = getObjectivesLimit();
  const hasTooMany = size(allItems) > limit;
  const extraSize = size(allItems) - limit;
  const cuttingPoint = limit - extraSize - 1;
  const items = hasTooMany ? allItems.slice(0, cuttingPoint) : allItems;
  const transformed = items.map((item) => {
    if (item.key === 'minimizeCost' && hasBalanceObjective)
      return [getObjectiveFunction(item), getObjectiveFunction(balanceObjective)];
    return getObjectiveFunction(item);
  });
  if (hasTooMany) {
    const toAdd = allItems.slice(cuttingPoint);
    const extraItems = toAdd.map((item) => getObjectiveFunction(item));
    let pairs = [];
    extraItems.forEach((item, index) => {
      pairs.push(item);
      if (index % 2 === 0) {
        transformed.push(pairs);
        pairs = [];
      }
    });
  }
  return transformed;
};

export const getConfigurations = (tourPlanner, types) => {
  const configuration = {};

  if (tourPlanner.solverConfiguration.terminationCriteria.enabled) {
    configuration.termination = tourPlanner.solverConfiguration.terminationCriteria.values;
  }

  const experimentalFeatures = [];

  const hasRecharges = types.some((type) => type.shifts?.some((shift) => shift.recharges));
  if (hasRecharges) {
    experimentalFeatures.push('recharges');
  }

  const hasMinStops = types.some((type) => type.limits?.stops?.minCount);
  if (hasMinStops) {
    experimentalFeatures.push('minStops');
  }

  if (experimentalFeatures.length > 0) {
    configuration.experimentalFeatures = experimentalFeatures;
  }

  return Object.keys(configuration).length > 0 ? configuration : null;
};

export const getFleetTypeItem = (
  index,
  vehicleAmount,
  vehicleDef,
  depotLocation,
  vrpTimes,
  tourPlanner,
  territories,
  strict,
  skills,
  currentJson,
  addVehicleLimit,
  user,
) => {
  const { start, end } = vrpTimes;
  const mixedRestrictions = currentJson?.[0]?.shifts?.[0]?.mixingRestrictions || {};
  const idSuffixParts = [];
  if (Array.isArray(territories) && territories.length > 0) {
    idSuffixParts.push(...territories.map((t) => t.id));
  }
  if (Array.isArray(skills) && skills.length > 0) {
    idSuffixParts.push(...skills);
  }
  const idSuffix = idSuffixParts.length ? idSuffixParts.join('_') : index;

  let cleanIdSuffix = idSuffix
    ?.replace(/(?:^|_)vehicle_/g, '_')
    ?.replace(/_{2,}/g, '_')
    ?.replace(/^_+|_+$/g, '');

  const parts = cleanIdSuffix.split('_');
  const uniqueParts = [];
  parts.forEach((part) => {
    if (!uniqueParts.includes(part)) uniqueParts.push(part);
  });
  cleanIdSuffix = uniqueParts.join('_');

  const startObj = tourPlanner?.start;
  const endObj = tourPlanner?.end;

  const type = {
    ...vehicleDef,
    amount: vehicleAmount,
    id: territories || skills ? `vehicle_${cleanIdSuffix}` : vehicleDef.id,
    profile: vehicleDef.profile || 'vehicle',
    shifts: [
      {
        start: {
          ...(depotLocation && { location: depotLocation }),
          time: start.format(dateTimeFormat),
          ...(startObj || {}),
        },
      },
    ],
    ...(typeof tourPlanner.vehicles?.[0]?.speedFactor !== 'undefined'
      ? { speedFactor: tourPlanner.vehicles[0].speedFactor }
      : {}),
  };

  if (tourPlanner.returnLocation.value) {
    type.shifts[0].end = {
      location: { ...tourPlanner.returnLocation.value },
      time: end.format(dateTimeFormat),
      ...(endObj || {}),
    };
  }

  if (!isEmpty(mixedRestrictions)) {
    type.shifts[0].mixingRestrictions = mixedRestrictions;
  } else if (!isProdEnv()) {
    const categories = user?.mapSettings?.categories;
    if (categories?.enabled && typeof categories.value === 'string') {
      const conflictingCategories = categories.value
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);

      type.shifts[0].mixingRestrictions = {
        level: 'tour',
        restrictions: [{ conflictingCategories }],
      };
    }
  }

  const tpVersion = user?.tpaVersion?.version;
  const isDevOrSit = tpVersion === 'dev' || tpVersion === 'sit';
  const evProfile = tourPlanner.vehicleProfiles?.[0]?.evProfile;
  const isElectricVehicle = tourPlanner.vehicleProfiles?.[0]?.isElectricVehicle;
  const appMode = getAppMode();
  const isEV = appMode.includes(APP_MODES.EV_PROFILES);

  if (isDevOrSit && isElectricVehicle && evProfile?.maxDistance && isEV) {
    type.shifts[0].recharges = {
      maxDistance: evProfile.maxDistance,
    };
  } else if (tourPlanner.recharges) {
    type.shifts[0].recharges = tourPlanner.recharges;
  }

  if (tourPlanner.breaks) {
    type.shifts[0].breaks = tourPlanner.breaks;
  }
  if (tourPlanner.restTimes) {
    type.shifts[0].restTimes = tourPlanner.restTimes;
  }
  if (tourPlanner.reloads) {
    type.shifts[0].reloads = tourPlanner.reloads;
  }

  const limits = tourPlanner.vehicles[0].limits;
  if (limits) {
    type.limits = {};

    if (limits.maxDistance?.enabled) {
      type.limits.maxDistance = limits.maxDistance.value;
    }

    if (limits.shiftTime?.enabled) {
      type.limits.shiftTime = limits.shiftTime.value;
    }

    if (limits.maxDrivingTime?.enabled) {
      type.limits.maxDrivingTime = limits.maxDrivingTime.value;
    }

    if (limits.maxStops?.enabled || limits.minStops?.enabled) {
      type.limits.stops = {};

      if (limits.maxStops?.enabled) {
        type.limits.stops.maxCount = { value: limits.maxStops.value };
      }

      if (limits.minStops?.enabled) {
        type.limits.stops.minCount = { value: limits.minStops.value };
      }
    }
    if (isEmpty(type.limits) || !addVehicleLimit) {
      delete type.limits;
    }
  }

  if (Array.isArray(territories) && territories.length > 0) {
    type.territories = {
      items: territories.map((t) => ({ id: t.id })),
      strict,
    };
  }

  if (Array.isArray(skills) && skills.length > 0) {
    type.skills = skills;
  }
  if (isDevOrSit && isElectricVehicle && evProfile?.profileId && isEV) {
    type.charging = {
      evProfileId: evProfile.profileId,
    };
  }

  return type;
};

const getFleetTypeItems = (
  isImperial,
  depotLocation,
  vrpTimes,
  tourPlanner,
  territories,
  strict,
  skills,
  currentJson,
  addVehicleLimit,
  user,
  onlyShiftLocations = false,
) => {
  if (onlyShiftLocations && currentJson && Array.isArray(currentJson)) {
    return currentJson.map((type) => {
      const shift = type.shifts[0];
      const endLocation = tourPlanner.returnLocation.value;
      shift.start = { ...shift.start, ...(depotLocation && { location: depotLocation }) };
      if (endLocation) {
        if (shift.end) shift.end = { ...shift.end, location: endLocation };
        else shift.end = { location: endLocation };
      }

      const shifts = [...type.shifts];
      shifts[0] = shift;
      return {
        ...type,
        shifts,
      };
    });
  }

  const terrSkillsLink = tourPlanner?.skillTerritoryAssociations || {};
  const hasTerritories = Array.isArray(territories) && territories.length > 0;
  const hasSkills = Array.isArray(skills) && skills.length > 0;
  const vehicleDef = getVRPVehicleDefinition(tourPlanner, isImperial);
  const vehicleAmount = Math.max(1, vehicleDef.amount || 2);
  const types = [];

  const territoryMap = {};
  if (hasTerritories) {
    territories.forEach((t) => {
      if (typeof t === 'object' && t.id) {
        territoryMap[t.id] = t;
      } else if (typeof t === 'string') {
        territoryMap[t] = { id: t };
      }
    });
  }

  const addedSkills = new Set();
  const addedTerritories = new Set();

  const usedIds = new Set();
  function ensureUniqueId(baseId) {
    let id = baseId;
    let counter = 2;
    while (usedIds.has(id)) {
      id = `${baseId}_${counter}`;
      counter++;
    }
    usedIds.add(id);
    return id;
  }

  Object.entries(terrSkillsLink).forEach(([skill, linkedTerritories]) => {
    const territoryObjs = Array.isArray(linkedTerritories)
      ? linkedTerritories.map((tid) => territoryMap[tid]).filter(Boolean)
      : [];

    const vehicleId = [skill, ...linkedTerritories].filter(Boolean).join('-');

    const item = getFleetTypeItem(
      vehicleId,
      vehicleAmount,
      vehicleDef,
      depotLocation,
      vrpTimes,
      tourPlanner,
      territoryObjs.length ? territoryObjs : undefined,
      strict,
      [skill],
      currentJson,
      addVehicleLimit,
      user,
    );
    item.id = ensureUniqueId(item.id);
    types.push(item);

    addedSkills.add(skill);
    territoryObjs.forEach((t) => addedTerritories.add(t.id));
  });

  if (hasSkills) {
    skills.forEach((skill) => {
      if (!addedSkills.has(skill)) {
        const vehicleId = skill;
        const item = getFleetTypeItem(
          vehicleId,
          vehicleAmount,
          vehicleDef,
          depotLocation,
          vrpTimes,
          tourPlanner,
          undefined,
          strict,
          [skill],
          currentJson,
          addVehicleLimit,
          user,
        );
        item.id = ensureUniqueId(item.id);
        types.push(item);
      }
    });
  }

  if (hasTerritories) {
    territories.forEach((t) => {
      const tid = typeof t === 'string' ? t : t.id;
      if (!addedTerritories.has(tid)) {
        const vehicleId = tid;
        const territoryObj = territoryMap[tid];
        const item = getFleetTypeItem(
          vehicleId,
          vehicleAmount,
          vehicleDef,
          depotLocation,
          vrpTimes,
          tourPlanner,
          [territoryObj],
          strict,
          undefined,
          currentJson,
          addVehicleLimit,
          user,
        );
        item.id = ensureUniqueId(item.id);
        types.push(item);
      }
    });
  }

  if (!hasSkills && !hasTerritories) {
    const item = getFleetTypeItem(
      'default',
      vehicleAmount,
      vehicleDef,
      depotLocation,
      vrpTimes,
      tourPlanner,
      undefined,
      strict,
      undefined,
      currentJson,
      addVehicleLimit,
      user,
    );
    item.id = ensureUniqueId(item.id);
    types.push(item);
  }

  return types;
};

function getVehicleProfiles(profiles) {
  return profiles.map((profile) => {
    const result = {
      name: profile.name,
      type: profile.fleetType,
    };

    if (profile?.traffic) {
      result.traffic = profile.traffic;
    }
    if (profile?.avoid) {
      result.avoid = profile.avoid;
    }
    if (profile?.options) {
      result.options = profile.options;
    }
    if (profile?.exclude) {
      result.exclude = profile.exclude;
    }

    if (profile?.ignoreRouteViolations) {
      result.ignoreRouteViolations = profile.ignoreRouteViolations;
    }
    if (profile?.matrix) {
      result.matrix = profile.matrix;
    }
    if (profile?.violationMapping) {
      result.violationMapping = profile.violationMapping;
    }

    if (profile?.departureTime) {
      result.departureTime = profile.departureTime;
    }

    return result;
  });
}

export function generateVRPPayload(
  jobs,
  depot,
  filename,
  tourPlanner,
  orders,
  territories,
  user,
  triggerVrp,
  jsonManual,
  isJson,
) {
  const currentJson = user?.currentJson;
  const currentFleetTypes = currentJson?.fleet?.types;
  const territoriesGroups = groupJobsPerTerritory(orders, territories.areaDetails, true);
  const depotLocation = getDepotLocation(depot);
  const vrpTimes = getVRPTimes(tourPlanner);
  const groups = tourPlanner?.groups;
  const cfg = tourPlanner?.configuration;
  const typesTp = !triggerVrp && !jsonManual && tourPlanner.types;
  const allSkills = Array.from(new Set(jobs.map((job) => job.skills || []).flat()));

  const onlyShiftLocations = !!isJson && tourPlanner.fileType === 'json' && !jsonManual;
  const types =
    typesTp ||
    getFleetTypeItems(
      user.distance === 'imperial',
      depotLocation,
      vrpTimes,
      tourPlanner,
      territoriesGroups,
      territories.strict,
      allSkills,
      currentFleetTypes,
      user?.mapSettings?.addVehicleLimit,
      user,
      onlyShiftLocations,
    );
  const profiles = getVehicleProfiles(tourPlanner.vehicleProfiles);

  if (tourPlanner.fileType === 'json' && currentJson && isJson) {
    const fleet = {
      ...currentJson.fleet,
      types,
      profiles,
    };
    const vrpPayload = {
      ...currentJson,
      fleet,
      plan: { jobs },
    };
    if (groups) {
      vrpPayload.plan = { groups, jobs };
    }
    return vrpPayload;
  }

  const vrpPayload = {
    configuration: {},
    fleet: {
      ...(typeof tourPlanner.vehicles?.[0]?.traffic !== 'undefined' &&
      tourPlanner.vehicles?.[0]?.traffic !== 'None'
        ? { traffic: tourPlanner.vehicles[0].traffic }
        : {}),
      types,
      profiles,
    },
    plan: { jobs },
  };

  if (groups) {
    vrpPayload.plan = { groups, jobs };
  }

  if (cfg) {
    vrpPayload.configuration = cfg;
  } else {
    delete vrpPayload.configuration;
  }

  const objectives = getObjectiveFunctions(tourPlanner, jobs, territories.areaDetails);

  if (objectives) {
    vrpPayload.objectives = objectives;
  }

  const configuration = getConfigurations(tourPlanner, types);
  if (configuration) {
    vrpPayload.configuration = configuration;
  }

  return vrpPayload;
}
